import { useLocation } from "react-router";
import { useGetMembershipShareAccessQuery } from "@lending/hooks/queries/MembershipShare/useGetMembershipShareAccessQuery";
import { useCurrentApplicationUuid } from "@lending/hooks/useCurrentApplicationUuid";
import { useFlowType } from "@lending/hooks/useFlowType";
import { getCurrentFlowStepId } from "@lending/navigation";
import { useAutoLoanFlowState } from "@lending/navigation/hooks/useAutoLoanFlowState";
import { getAutoLoanCanonicalRoutePolicy } from "./autoLoanRoutePolicy";
import type { AutoLoanApplicationContext } from "./applicationNavigationContext";

function useResolvedAutoLoanApplication() {
  const location = useLocation();
  const flowType = useFlowType();
  const applicationUuid = useCurrentApplicationUuid();
  const autoLoanFlowState = useAutoLoanFlowState();
  const { application, isLoading, isError, shouldCollectAutoRequestedAmount } = autoLoanFlowState;
  const isMembershipShareState =
    application?.state === "membership_selection" ||
    application?.state === "membership_share_pending";
  const membershipShareAccessQuery = useGetMembershipShareAccessQuery({
    enabled: isMembershipShareState,
  });
  const currentStepId = applicationUuid
    ? getCurrentFlowStepId(location.pathname, "auto-loan", flowType, applicationUuid)
    : null;
  const canonicalStepId: string | null = application
    ? getAutoLoanCanonicalRoutePolicy(
        application,
        currentStepId,
        membershipShareAccessQuery.data,
        flowType,
        shouldCollectAutoRequestedAmount
      ).canonicalStepId
    : null;

  const shouldAlwaysEnforceRoute =
    isMembershipShareState && Boolean(membershipShareAccessQuery.data);

  const autoLoanContext: AutoLoanApplicationContext = {
    hasVehicleSelected: autoLoanFlowState.hasVehicleSelected,
    isRefinance: autoLoanFlowState.isRefinance,
    isAutoRequestedAmountEnabled: autoLoanFlowState.isAutoRequestedAmountEnabled,
  };

  return {
    isLoading: isLoading || membershipShareAccessQuery.isLoading,
    isError: isError || membershipShareAccessQuery.isError,
    canonicalStepId,
    currentStepId,
    applicationState: application?.state,
    application,
    autoLoanContext: autoLoanContext,
    shouldAlwaysEnforceRoute,
    accountApplicationUuid: application?.account_application_uuid,
  };
}

export { useResolvedAutoLoanApplication };
